import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Box,
  FormControl,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { getRoles } from "redux/actions/Master";
import { useNavigate } from "react-router-dom";
import { AppInfoView, AppsPagination } from "@crema";
import AppsContainer from "@crema/core/AppsContainer";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { RoleData } from "types/models/master/MasterApp";
import { isModuleAuthorized } from "@crema/utility/Utils";
import RoleTable from "./RoleTable";


interface MasterState {
  roles: RoleData[];
  roleCount: number;
}

interface AppState {
  master: MasterState;
}

const Roles = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rowsPerPage = 10;

  const [searchField, setSearchField] = useState("all");
  const [search, setSearchQuery] = useState<string>("");
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<string>("All Roles");

  const rolesState = useSelector((state: AppState) => state.master);
  const roles = rolesState.roles || [];

  const searchFields = [
    { label: "All", value: "all" },
    { label: "Role", value: "role" },
    { label: "Module Type", value: "moduleType" }
  ];

  useEffect(() => {
    dispatch(getRoles());
    dispatch(getRoles()); 
  }, [dispatch]);

  const onSearchRole = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  const onPageChange = (
    event: React.ChangeEvent<unknown> | null,
    value: number
  ) => {
    setPage(value);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
    setPage(0);
  };

  const handleAddRoleClick = () => {
    navigate("/master/manageRole/roleCreate");
  };

  const filteredRoles = roles.filter((item) => {
    const searchTerm = search.trim().toLowerCase();
    const matchesFilter =
      filter === "All Roles" || item.role === filter;

    if (!searchTerm) return matchesFilter;

    if (searchField === "all") {
      return (
        matchesFilter &&
        searchFields
          .filter((f) => f.value !== "all")
          .some(({ value }) => {
            const fieldValue = item[value];
            return fieldValue?.toString().toLowerCase().includes(searchTerm);
          })
      );
    } else {
      const fieldValue = item[searchField];
      return (
        matchesFilter &&
        fieldValue?.toString().toLowerCase().includes(searchTerm)
      );
    }
  });

  const paginatedRoles = filteredRoles.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  return (
    <>
      <AppsContainer
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {messages["sidebar.master.roles"]}
            </Box>

            {isModuleAuthorized("Add Role") && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddRoleClick}
                sx={{ flexShrink: 0 }}
              >
                Add Role
              </Button>
            )}
          </Box>
        }
        fullView
      >
        <AppsHeader>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControl
                fullWidth
                sx={{
                  width: 150,
                  height: "50px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
               
              </FormControl>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                justifyContent: "flex-end",
                width: 1,
              }}
            >
              <TextField
                select
                size="small"
                value={searchField}
                onChange={(e) => {
                  setSearchField(e.target.value);
                  setSearchQuery("");
                  setPage(0);
                }}
                SelectProps={{ native: true }}
              >
                {searchFields.map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </TextField>
              <TextField
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => onSearchRole(e.target.value)}
                placeholder={messages["common.searchHere"] as string}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {search ? (
                        <IconButton
                          onClick={() => onSearchRole("")}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 200 }}
              />
            </Box>
          </Box>
        </AppsHeader>

        <AppsContent
          sx={{
            paddingTop: 2.5,
            paddingBottom: 2.5,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <RoleTable roles={paginatedRoles} />
          <AppsPagination
            rowsPerPage={rowsPerPage}
            count={filteredRoles.length}
            page={page}
            onPageChange={onPageChange}
          />
        </AppsContent>
      </AppsContainer>

      <AppInfoView />
    </>
  );
};

export default Roles;
